import {
    Button,
    Card,
    Icon,
    MenuItem,
    Snackbar,
    Typography,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { getMovieBySlug } from "../api/trakt";
import {
    getUser,
    joinSharedList,
    usePremium,
    useSharedLists,
} from "../api/firebase";
import Poster from "../components/Poster";
import { useToolbarOptions } from "../context/toolbarOptions";
import { useAuth } from "../context/auth";
import {
    MAX_SHARED_LISTS_FOR_NON_PREMIUM,
    MAX_SHARED_LISTS_FOR_PREMIUM,
} from "../util/premium";
import { logEvent } from "../util/logUtil";
import { getSharedListUrl } from "../util/util";

import "./SharedListScreen.css";

const db = window.firebase.firestore();

const SharedListScreen = () => {
    const { id } = useParams();
    const authState = useAuth();
    const { setToolbarOptions } = useToolbarOptions();
    const history = useHistory();

    const [sharedList, setSharedList] = useState(null);
    const [sharedListExists, setSharedListExists] = useState(true);
    const [message, setMessage] = useState("");

    const isPremium = usePremium();

    useEffect(() => {
        const unsubscribe = db
            .collection("sharedLists")
            .doc(id)
            .onSnapshot((doc) => {
                if (doc.exists) {
                    setSharedList({ id: doc.id, ...doc.data() });
                } else {
                    setSharedListExists(false);
                }
            });

        return () => unsubscribe();
    }, [id]);

    useApplyToolbarOptions(sharedList, setToolbarOptions);
    const handleJoin = useJoinListener(
        sharedList,
        authState,
        isPremium,
        setMessage,
        history
    );

    const isMember =
        authState &&
        sharedList &&
        authState.isAuthenticated &&
        sharedList.members &&
        sharedList.members.includes(authState.user.uid);

    return (
        <div className="sharedListContainer">
            {sharedList && (
                <div className="sharedList">
                    <Items sharedListId={sharedList.id} />
                    {!isMember && (
                        <div className="sharedListJoinButton">
                            <Button
                                variant="contained"
                                disabled={isPremium === null}
                                onClick={handleJoin}
                            >
                                Join
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {!sharedListExists && (
                <div className="sharedListNotExists">
                    <Typography variant="h5" align="center">
                        Shared List does not exist.
                    </Typography>
                </div>
            )}
            <Snackbar
                message={message}
                open={message !== ""}
                onClose={() => setMessage("")}
                autoHideDuration={1500}
            />
        </div>
    );
};

const useApplyToolbarOptions = (sharedList, setToolbarOptions) => {
    useEffect(() => {
        const items = [];
        if (navigator.canShare) {
            items.push(
                <MenuItem
                    key="share"
                    onClick={async () => {
                        try {
                            await navigator.share({
                                title: sharedList.name,
                                url: getSharedListUrl(sharedList.id),
                            });
                        } catch (e) {}
                    }}
                >
                    <Icon>share</Icon>
                </MenuItem>
            );
        }
        if (sharedList) {
            setToolbarOptions({
                title: sharedList.name,
                items: items,
            });
        } else {
            setToolbarOptions({
                title: "",
                items: [],
            });
        }
    }, [setToolbarOptions, sharedList]);
};

const useJoinListener = (
    sharedList,
    authState,
    isPremium,
    setMessage,
    history
) => {
    const sharedLists = useSharedLists();
    const numSharedLists = sharedLists ? sharedLists.length : 0;

    return () => {
        const doJoin = async () => {
            try {
                await joinSharedList(sharedList, authState.user.uid);
                setMessage("Joined");
            } catch (e) {
                setMessage("Error joining list.");
            }
        };

        logEvent("Join shared list button clicked");

        if (authState && authState.isAuthenticated) {
            if (isPremium) {
                if (numSharedLists < MAX_SHARED_LISTS_FOR_PREMIUM) {
                    doJoin();
                } else {
                    setMessage(
                        `You have reached the limit of ${MAX_SHARED_LISTS_FOR_PREMIUM} shared lists.`
                    );
                }
            } else {
                if (numSharedLists < MAX_SHARED_LISTS_FOR_NON_PREMIUM) {
                    doJoin();
                } else {
                    setMessage(
                        `You have reached the limit of ${MAX_SHARED_LISTS_FOR_NON_PREMIUM} shared lists. Upgrade to Movie Pal Premium through our Android app to be able to join up to ${MAX_SHARED_LISTS_FOR_PREMIUM} shared lists.`
                    );
                }
            }
        } else {
            history.push("/login");
        }
    };
};

const Items = (props) => {
    const { sharedListId } = props;

    const [items, setItems] = useState(null);

    useEffect(() => {
        const unsubscribe = db
            .collection("sharedLists")
            .doc(sharedListId)
            .collection("items")
            .onSnapshot((querySnapshot) => {
                const docs = [];
                querySnapshot.forEach((doc) =>
                    docs.push({ id: doc.id, ...doc.data() })
                );
                setItems(docs);
            });

        return () => unsubscribe();
    }, [sharedListId]);

    return (
        <>{items && items.map((item) => <Item key={item.id} item={item} />)}</>
    );
};

const Item = (props) => {
    const { item } = props;

    const [movie, setMovie] = useState(null);
    const [addedBy, setAddedBy] = useState("");

    useEffect(() => {
        const loadMovie = async () => {
            try {
                setMovie(await getMovieBySlug(item.traktId));
            } catch (e) {
                console.log(e);
            }
        };

        const loadUser = async () => {
            try {
                setAddedBy(await getUser(item.addedBy));
            } catch (e) {
                console.log(e);
            }
        };

        loadMovie();
        loadUser();
    }, [item]);

    const inner = () => (
        <Card className="sharedListItem">
            {movie && <Poster item={movie} width={80} height={120} />}
            <div style={{ margin: "5px" }}>
                <Typography variant="h6">{movie && movie.title}</Typography>
                <Typography>Added by {addedBy.name}</Typography>
                {new Date(item.addedOn.seconds * 1000).toLocaleString()}
            </div>
        </Card>
    );

    return (
        <>
            {movie ? (
                <Link to={`/movies/${movie.ids.slug}`}>{inner()}</Link>
            ) : (
                inner()
            )}
        </>
    );
};

export default SharedListScreen;
